/**
 * Turning a MIB object into a custom metric, for the picker.
 *
 * Pure, so the rules are testable: which instance a scalar is read at, what
 * labels a table's rows, and when the device disagrees with its own MIB.
 */

import type { CustomMetricInput, MibObject, ProbeRow } from "./api.js";

/**
 * IF-MIB ifName. Offered by number for any table indexed by ifIndex, since
 * that is how vendors key per-port tables and a port's name is what anyone
 * wants on the legend -- whether or not IF-MIB itself was uploaded.
 */
export const IF_NAME_OID = "1.3.6.1.2.1.31.1.1.1.1";

export interface LabelChoice {
  oid: string;
  name: string;
}

/** The OID collection reads: a scalar's value lives at instance .0. */
export function collectedOid(object: MibObject): string {
  return object.is_column ? object.oid : `${object.oid}.0`;
}

export function metricFromObject(object: MibObject, label: LabelChoice | null): CustomMetricInput {
  return {
    name: object.name,
    oid: collectedOid(object),
    is_table: object.is_column,
    label_oid: object.is_column ? label?.oid ?? null : null,
    kind: object.kind === "counter" ? "counter" : "gauge",
    // Scale is never inferred from a MIB. UNITS is free text ("0.1 degrees",
    // "hundredths of a percent") and reading a multiplier out of it would be
    // a guess that lands permanently in history.
    scale: 1,
    unit: object.units ? object.units.trim().slice(0, 24) : null,
    mib_module: object.module_name,
    mib_object: object.name,
  };
}

/** Columns that could name each row of the object's table. */
export function labelCandidates(object: MibObject, siblings: readonly MibObject[]): LabelChoice[] {
  if (!object.is_column) return [];
  const choices: LabelChoice[] = [];
  if (object.index_names?.includes("ifIndex")) choices.push({ oid: IF_NAME_OID, name: "ifName (IF-MIB)" });
  for (const s of siblings) {
    if (s.oid === object.oid || !s.is_column || s.kind !== "text") continue;
    choices.push({ oid: s.oid, name: s.name });
  }
  return choices;
}

/**
 * The likeliest label, or none. Only a suggestion: the picker shows each
 * candidate's live values, so a wrong one is visible before it is chosen.
 */
export function suggestLabel(choices: readonly LabelChoice[]): LabelChoice | null {
  return (
    choices.find((c) => c.oid === IF_NAME_OID) ??
    choices.find((c) => /name|descr|port|label/i.test(c.name)) ??
    null
  );
}

/**
 * Why the device's answer contradicts the MIB, or null if it does not.
 *
 * Vendors' MIBs are frequently wrong about their own agents. Collection
 * judges by what the agent sends, so this tells the person now what
 * collection would otherwise report as an error on every poll.
 */
export function disagreement(kind: "gauge" | "counter", rows: readonly ProbeRow[]): string | null {
  const row = rows[0];
  if (!row) return null;
  if (kind === "counter" && !row.counter) {
    return `The device answers ${row.type}, not a counter, so a rate cannot be taken. Collect it as a gauge.`;
  }
  if (!row.numeric) return `The device answers ${row.type} (${row.text}), which cannot be graphed.`;
  return null;
}
